// Resize Shopify CDN images on the fly.
//
// Shopify's image CDN accepts `width`/`height` query params and returns
// a smaller, faster variant — much cheaper than shipping a 2000px
// original for a 280px-wide product card. We also append `crop=center`
// when both dimensions are given so we don't get letterboxing.
//
// Falls back to the original URL if it isn't a Shopify CDN image
// (defensive — covers any future external image sources).

export function shopifyImg(
  url: string | undefined | null,
  opts: { width?: number; height?: number; crop?: "center" | "top" | "bottom" | "left" | "right" } = {},
): string {
  if (!url) return "";
  if (!/cdn\.shopify\.com|myshopify\.com/.test(url)) return url;

  try {
    const u = new URL(url);
    const dpr = typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 2) : 1;
    if (opts.width) u.searchParams.set("width", String(Math.round(opts.width * dpr)));
    if (opts.height) u.searchParams.set("height", String(Math.round(opts.height * dpr)));
    if (opts.width && opts.height && opts.crop) u.searchParams.set("crop", opts.crop);
    return u.toString();
  } catch {
    return url;
  }
}

// Build a `srcset` for responsive loading at common card widths.
export function shopifySrcSet(
  url: string | undefined | null,
  widths: number[] = [320, 480, 640, 800],
): string | undefined {
  if (!url || !/cdn\.shopify\.com|myshopify\.com/.test(url)) return undefined;
  return widths
    .map((w) => `${shopifyImg(url, { width: w })} ${w}w`)
    .join(", ");
}
